/**
 * FilterBar — global filter strip (IA §1.2, §9).
 *
 * Desktop (≥768px): single row with Date + Role/Cohort/Channel segmented
 * controls + Users + (optional) Granularity segmented control + Reset.
 * Segmented controls replace the old two-click Select dropdowns for fields
 * with ≤4 options so changing a filter is a single click.
 *
 * Mobile (≤767px): a Filters button opens <FilterModal /> which presents the
 * controls vertically stacked. Changes in the modal live-apply — closing
 * the modal simply hides it.
 *
 * Granularity is rendered inline rather than as the old `<GranularitySubbar />`
 * row — one strip total.
 *
 * Tabs where an axis does not apply: see `inapplicable` below and
 * lib/filter-applicability.ts. The control stays on screen, greyed and
 * non-interactive, carrying the reason.
 *
 * Every control writes the merged filter straight to the URL via
 * `useUrlFilters().setPending` (Bug F1 — live-apply). No Apply button;
 * TanStack Query dedupes rapid successive calls. Reset clears the URL back
 * to `?`.
 */

import { useState } from 'react';
import { RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SegmentedControl } from '@/components/ui/segmented';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { useViewport } from '@/hooks/use-viewport';
import type { InapplicableAxes } from '@/lib/filter-applicability';
import type { Filters } from '@/lib/filters-schema';
import { resolveRange } from '@/lib/filters-schema';
import { effectiveGranularity } from '@/lib/granularity';

import { DateRangeControl } from './DateRangeControl';
import {
  ADMINS_OPTIONS,
  FilterModal,
  countActiveFilters,
  INPUT_MODE_OPTIONS,
  REQUEST_TYPE_OPTIONS,
  SURFACE_OPTIONS,
} from './FilterModal';
import { UserMultiSelect } from './UserMultiSelect';

type Granularity = Filters['granularity'];

const ROLE_OPTIONS: ReadonlyArray<{ value: Filters['role']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'rm', label: 'RM' },
  { value: 'non-rm', label: 'Non-RM' },
];

// BUG E-Cohort — cohort dropped 'all' from its enum; "no segmentation" is now
// the absent field (filters.cohort === undefined). The UI keeps an 'all'
// sentinel option because a segmented control needs a concrete value per
// tab; `cohortUiToFilter` / `cohortFilterToUi` bridge between the two.
type CohortUiValue = 'all' | 'active' | 'engaged' | 'power';
const COHORT_OPTIONS: ReadonlyArray<{ value: CohortUiValue; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'power', label: 'Power' },
];
const cohortFilterToUi = (v: Filters['cohort']): CohortUiValue => v ?? 'all';
const cohortUiToFilter = (v: CohortUiValue): Filters['cohort'] =>
  v === 'all' ? undefined : v;

// BUG G2 — `group_dm` removed. Coach isn't used in group DMs, so the
// option was confusing. Legacy URLs with `channel=group_dm` are coerced
// back to `any` by the schema preprocess in filters-schema.ts.
const CHANNEL_OPTIONS: ReadonlyArray<{ value: Filters['channel']; label: string }> = [
  { value: 'any', label: 'Any' },
  { value: 'dm', label: 'DM' },
  { value: 'channel', label: 'Channel' },
];

const GRANULARITY_OPTIONS: ReadonlyArray<{ value: Granularity; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

// DayFilter — replaces the old Bug A7 "Show weekends" checkbox. Three tabs
// so the user can spot-check Mon-Fri, Sat-Sun, or everything in one click.
const DAY_FILTER_OPTIONS: ReadonlyArray<{ value: Filters['dayFilter']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekends', label: 'Weekends' },
];

export interface FilterBarProps {
  /**
   * The axes that cannot describe the current tab's population, each with the
   * short reason shown to the user. Decided once per pathname by
   * `inapplicableAxes()` in DashboardLayout and handed to BOTH surfaces — the
   * desktop bar below and the mobile `<FilterModal />`, which used to get no
   * such signal at all.
   *
   * A listed control is rendered DISABLED, not unmounted: it keeps showing the
   * inherited value (which is real, and does bite on other tabs) while saying
   * it cannot narrow this one. See lib/filter-applicability.ts for the two
   * reasons that beat unmounting — a measured 42px layout jump, and the fact
   * that a vanished control corrects no mental model.
   *
   * The URL contract is unaffected either way: `useUrlFilters` re-emits
   * whatever `location.search` holds, so an inherited value round-trips
   * through a tab that ignores it. Contrast `filter-channel` below, which is
   * wrapped in a hidden div for a reason of its own.
   */
  inapplicable?: InapplicableAxes;
}

const PRESET_LABELS: Record<NonNullable<Filters['preset']>, string> = {
  '24h': '24h',
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'All time',
  custom: 'Custom',
};

function formatPresetLabel(f: Filters): string {
  if (f.preset === 'custom' && f.from && f.to) {
    // Mobile audit 2026-04-24: the pill used to strip the year
    // (`iso.slice(5)` → "04-20 → 04-24"), which was ambiguous for
    // year-crossing ranges and plain confusing after January. Keep the
    // compact "MM-DD → MM-DD" form when both endpoints land in the
    // current year (the common case), but include the 2-digit year on
    // either side when the range straddles a boundary so users can still
    // tell "04-20 → 04-24" from "12-20/24 → 01-05/25".
    const currentYear = new Date().getUTCFullYear().toString();
    const fromYear = f.from.slice(0, 4);
    const toYear = f.to.slice(0, 4);
    const sameCurrent = fromYear === currentYear && toYear === currentYear;
    const fmt = (iso: string) =>
      sameCurrent ? iso.slice(5) : `${iso.slice(5)}/${iso.slice(2, 4)}`;
    return `${fmt(f.from)} → ${fmt(f.to)}`;
  }
  return f.preset ? PRESET_LABELS[f.preset] : '30d';
}

export function FilterBar({ inapplicable = {} }: FilterBarProps) {
  const { filters, pendingFilters, setPending, reset } = useUrlFilters();
  const { isMobile } = useViewport();
  const [modalOpen, setModalOpen] = useState(false);

  const activeCount = countActiveFilters(filters);
  const presetLabel = formatPresetLabel(filters);

  if (isMobile) {
    return (
      <div
        className="flex items-center gap-2 border-b bg-card px-3 py-2"
        data-testid="filter-bar"
        data-variant="mobile"
      >
        <Button
          variant="outline"
          onClick={() => setModalOpen(true)}
          // Overview P1-4: after the modal closed, focus returned to this
          // button and its focus ring (purple) stayed visible — users read
          // that as "button is stuck active". `:focus-visible` already
          // restricts the ring to keyboard focus on modern browsers, but
          // React's synthetic focus events during the modal's Radix focus-
          // trap teardown sometimes leave the button DOM-focused even
          // after the pointer is gone. Dropping focus on mouse-up gives
          // us the behaviour everyone expected: ring only on Tab, never
          // on click.
          onMouseUp={(e) => e.currentTarget.blur()}
          className="min-h-11 flex-1 justify-start gap-2"
          data-testid="filter-bar-open-modal"
        >
          <span className="truncate text-sm font-medium">{presetLabel}</span>
          <span className="text-muted-foreground text-xs">
            · Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </span>
        </Button>

        {/*
          Bug F1: Apply button removed — filter changes live-apply as the
          user taps a control, and the modal's Close/Reset is enough.
        */}
        <FilterModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          pendingFilters={pendingFilters}
          setPending={setPending}
          onReset={reset}
          activeCount={activeCount}
          inapplicable={inapplicable}
        />
      </div>
    );
  }

  // Desktop: everything in a single wrapping row so narrow windows still work
  // without a second bar.
  const range = resolveRange(pendingFilters);
  const effective = effectiveGranularity(
    pendingFilters.granularity,
    range.from,
    range.to,
  );

  // The Auto segment used to inline a short hint ("Auto d" / "Auto h" /
  // "Auto w") so users could see what granularity Auto resolved to without
  // a separate text span. That hint is gone now: the bar wraps cleanly at
  // 1280px so we don't need to squeeze a 2ch hint into the segmented
  // control to keep Reset on row 1. The effective granularity is still
  // surfaced in each chart's own header — that's the right place for it.
  const granularityOptions = GRANULARITY_OPTIONS;
  // Layout contract:
  //   - Reset is pinned to the top-right corner of the bar regardless of how
  //     much content sits in the left side (`items-start` on the outer flex
  //     keeps it at row-1 when the inner cluster wraps onto a second row).
  //   - The inner `flex-1 flex-wrap` cluster contains every filter control
  //     including Granularity/DayFilter; controls at the END of that cluster
  //     (Granularity, DayFilter) are the natural overflow targets and break
  //     to a new line LEFT-aligned when the bar narrows below ~1280px.
  return (
    <div
      className="flex items-start gap-2 px-3 py-1.5 border-b bg-card"
      data-testid="filter-bar"
      data-variant="desktop"
    >
      <div className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-2">
      <DateRangeControl
        inapplicableReason={inapplicable.window}
        value={{
          preset: pendingFilters.preset,
          from: pendingFilters.from,
          to: pendingFilters.to,
        }}
        onChange={(next) =>
          setPending({ preset: next.preset, from: next.from, to: next.to })
        }
      />

      <span
        className="h-5 w-px bg-border/60"
        aria-hidden="true"
      />

      <SegmentedControl
        id="filter-role"
        label="Role:"
        value={pendingFilters.role}
        onChange={(v) => setPending({ role: v })}
        options={ROLE_OPTIONS}
        size="xs"
        data-testid="filter-role"
      />

      <SegmentedControl
        id="filter-cohort"
        label="Cohort:"
        labelHint={
          <div className="space-y-1">
            <p className="font-medium text-foreground">How cohorts are counted</p>
            <ul className="space-y-0.5">
              <li>
                <strong>All</strong> — no cohort segmentation.
              </li>
              <li>
                <strong>Active</strong> — users with ≥1 request in the window.
              </li>
              <li>
                <strong>Engaged</strong> — users active on ≥3 distinct days
                within the window.
              </li>
              <li>
                <strong>Power</strong> — users active on ≥5 distinct days AND
                with ≥10 requests in the window.
              </li>
            </ul>
          </div>
        }
        value={cohortFilterToUi(pendingFilters.cohort)}
        onChange={(v) => setPending({ cohort: cohortUiToFilter(v) })}
        options={COHORT_OPTIONS}
        size="xs"
        data-testid="filter-cohort"
      />

      <SegmentedControl
        id="filter-surface"
        label="Channel:"
        value={pendingFilters.surface}
        onChange={(v) => setPending({ surface: v })}
        options={SURFACE_OPTIONS}
        size="xs"
        inapplicableReason={inapplicable.surface}
        data-testid="filter-surface"
      />

      <SegmentedControl
        id="filter-request-type"
        label="Type:"
        labelHint={
          <div className="space-y-1">
            <p className="font-medium text-foreground">Mr. Edge vs Inbox</p>
            <p>
              <strong>Inbox</strong> — the turn recorded at least one task for
              the RM. <strong>Mr. Edge</strong> — it did not.
            </p>
            <p>
              A turn is recognised as a capture turn only from 18 Aug 2026.
              Capture itself started on 11 Aug 2026 without that marking, so a
              capture turn from that week is counted as Mr. Edge.
            </p>
          </div>
        }
        value={pendingFilters.requestType}
        onChange={(v) => setPending({ requestType: v })}
        options={REQUEST_TYPE_OPTIONS}
        size="xs"
        inapplicableReason={inapplicable.requestType}
        data-testid="filter-request-type"
      />

      <SegmentedControl
        id="filter-input-mode"
        label="Input:"
        labelHint={
          <div className="space-y-1">
            <p className="font-medium text-foreground">
              Text is an assumption, not a measurement
            </p>
            <p>
              A turn is counted as <strong>Voice</strong> only when it was
              explicitly recorded as dictated. Every other turn — including all
              turns before 6 Jul 2026 and every web turn — is counted as{' '}
              <strong>Text</strong> because nothing was recorded either way.
            </p>
            <p>This overstates Text and understates Voice.</p>
          </div>
        }
        value={pendingFilters.inputMode}
        onChange={(v) => setPending({ inputMode: v })}
        options={INPUT_MODE_OPTIONS}
        size="xs"
        inapplicableReason={inapplicable.inputMode}
        data-testid="filter-input-mode"
      />

      {/*
        Admins (2026-08-19). Global, like every control in this bar — it
        applies to every tab, not to the one that surfaced the problem.
        Carries a VISIBLE label (no `hideLabel`): a control that silently
        drops the busiest capture user from every number is the last one
        whose name should be readable only by a screen reader.
      */}
      <SegmentedControl
        id="filter-admins"
        label="Admins:"
        labelHint={
          <div className="space-y-1">
            <p className="font-medium text-foreground">
              Whose turns are counted
            </p>
            <p>
              <strong>Excluded</strong> is the default, and is how every number
              on every tab was counted before this control existed.{' '}
              <strong>Included</strong> admits the admins&apos; own rows.
            </p>
            <p>
              Only turns carrying an admin&apos;s <strong>Slack id</strong> are
              affected. A web turn carries none, so an admin&apos;s web turns
              are counted either way.
            </p>
          </div>
        }
        value={pendingFilters.admins}
        onChange={(v) => setPending({ admins: v })}
        options={ADMINS_OPTIONS}
        size="xs"
        data-testid="filter-admins"
      />

      {/*
        Channel filter is hidden in the desktop bar (per user 2026-04-26 —
        non-critical, almost always left at "Any"). Wrapped in a hidden
        div so the entire control (label included) is
        `display: none` but stays in the DOM — keeps the URL contract
        round-tripping `channel`. Mobile FilterModal still surfaces it,
        under the same "Slack conversation:" name: this control filters the
        Slack conversation a turn happened in (DM vs channel), which is a
        different axis from the `surface` control above, and calling both
        "Channel" was ambiguous.
      */}
      <div className="hidden">
        <SegmentedControl
          id="filter-channel"
          label="Slack conversation:"
          value={pendingFilters.channel}
          onChange={(v) => setPending({ channel: v })}
          options={CHANNEL_OPTIONS}
          size="xs"
          data-testid="filter-channel"
        />
      </div>

      <span
        className="h-5 w-px bg-border/60"
        aria-hidden="true"
      />

      <UserMultiSelect
        admins={pendingFilters.admins}
        value={pendingFilters.users}
        onChange={(users) => setPending({ users })}
      />

      {/*
        Granularity + DayFilter sit at the END of the inner cluster so they
        are the natural overflow targets — when the bar narrows below
        ~1280px these wrap to row 2 left-aligned (per user spec, easier to
        scan than a centered/right-aligned overflow). DayFilter is hidden at
        weekly (Sat/Sun already roll into the Mon row) and hourly (the chart
        sits inside a single day) — Quality P1-04 / Briefs P2-5 history.

        DayFilter used to ride inside Granularity's per-route gate and so
        vanished on /feedback and /jira with it. It should not have: it is a
        weekday/weekend ROW predicate (`weekendExclusionSQL`), applied on
        those two tabs like every other, so hiding it there hid a control
        that works. Now that "does this axis apply here?" is one declaration,
        each axis answers for itself; whole campaigns disable the day filter.
      */}
      <SegmentedControl
        id="filter-granularity"
        label="Granularity:"
        value={pendingFilters.granularity}
        onChange={(v) => setPending({ granularity: v })}
        options={granularityOptions}
        size="xs"
        inapplicableReason={inapplicable.granularity}
        data-testid="granularity-subbar"
      />
      {(inapplicable.dayFilter || (effective !== 'weekly' && effective !== 'hourly')) && (
        <SegmentedControl
          inapplicableReason={inapplicable.dayFilter}
          id="filter-day"
          label="Days:"
          value={pendingFilters.dayFilter}
          onChange={(v) => setPending({ dayFilter: v })}
          options={DAY_FILTER_OPTIONS}
          size="xs"
          data-testid="filter-day-filter"
        />
      )}
      </div>

      {/*
        Reset is OUTSIDE the wrapping cluster so `items-start` on the outer
        flex pins it to the top row regardless of how the inner cluster
        wraps. Icon-only below xl, label revealed at ≥1280px (Overview P3-4).
      */}
      <Button
        size="sm"
        variant="outline"
        onClick={reset}
        aria-label="Reset filters"
        title="Reset filters"
        className="h-8 shrink-0 gap-1.5 w-8 p-0 xl:w-auto xl:px-2.5"
        data-testid="filter-reset"
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" />
        <span className="hidden xl:inline text-xs">Reset</span>
      </Button>
    </div>
  );
}
